import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/utils/cn';

type Snap = 'half' | 'full';

// Sheet height per snap point, in dynamic viewport height units
const SNAP_HEIGHT: Record<Snap, number> = { half: 55, full: 92 };
// Drag distance (px) that switches snap point / dismisses
const SNAP_THRESHOLD = 60;
const CLOSE_THRESHOLD = 120;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  /** Snap point the sheet opens at. Defaults to `half`. */
  initialSnap?: Snap;
  /**
   * Let the content own layout and scrolling (no body padding/scroll), e.g.
   * a panel with its own pinned footer.
   */
  fullBleed?: boolean;
}

/**
 * Bottom sheet modal for mobile with half/full snap points: drag the handle
 * up to expand, down to collapse, and down from half to dismiss.
 */
export function BottomSheet({
  isOpen,
  onClose,
  children,
  title,
  description,
  initialSnap = 'half',
  fullBleed = false,
}: BottomSheetProps) {
  const [snap, setSnap] = useState<Snap>(initialSnap);
  const [dragDelta, setDragDelta] = useState(0); // px, positive = down
  const dragStartY = useRef<number | null>(null);

  // Prevent body scroll when open; reopen at the initial snap point
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSnap(initialSnap);
      setDragDelta(0);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialSnap]);

  const handlePointerDown = (e: React.PointerEvent) => {
    dragStartY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragStartY.current === null) return;
    setDragDelta(e.clientY - dragStartY.current);
  };

  const handlePointerUp = () => {
    if (dragStartY.current === null) return;
    dragStartY.current = null;

    if (dragDelta < -SNAP_THRESHOLD) {
      setSnap('full');
    } else if (snap === 'full' && dragDelta > SNAP_THRESHOLD) {
      // A long fling from full dismisses; a short one collapses to half
      if (dragDelta > window.innerHeight * 0.5) onClose();
      else setSnap('half');
    } else if (snap === 'half' && dragDelta > CLOSE_THRESHOLD) {
      onClose();
    }
    setDragDelta(0);
  };

  const isDragging = dragStartY.current !== null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/25 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={onClose}
        />

        <Dialog.Content
          onOpenAutoFocus={(e) => {
            // Focus the sheet itself so no control opens with a focus ring
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.focus();
          }}
          className={cn(
            'fixed bottom-0 left-0 right-0 z-50 max-h-[92dvh]',
            'bg-surface rounded-t-3xl shadow-monta-lg ring-1 ring-ink/10',
            'flex flex-col overflow-hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            'duration-300'
          )}
          style={{
            height: `calc(${SNAP_HEIGHT[snap]}dvh - ${dragDelta}px)`,
            transition: isDragging
              ? 'none'
              : 'height 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          {/* Drag handle - enlarged hit area for easier grabbing */}
          <div
            className="flex shrink-0 justify-center items-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className="w-10 h-1.5 bg-ink/15 rounded-full" />
          </div>

          {/* Header - Title and Description are always present for a11y */}
          {title ? (
            <div className="shrink-0 px-5 pb-3 border-b border-ink/5">
              <Dialog.Title className="font-heading font-semibold text-xl tracking-tight text-ink">
                {title}
              </Dialog.Title>
            </div>
          ) : (
            <Dialog.Title className="sr-only">Dialog</Dialog.Title>
          )}
          <Dialog.Description className="sr-only">
            {description || title || 'Dialog content'}
          </Dialog.Description>

          {/* Body */}
          <div
            className={cn(
              'flex-1 min-h-0',
              fullBleed
                ? 'flex flex-col'
                : 'overflow-y-auto overscroll-contain px-4 py-4'
            )}
            style={
              fullBleed
                ? undefined
                : { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }
            }
          >
            {children}
          </div>

          <Dialog.Close
            className="absolute top-3 right-3 p-2 rounded-full bg-ink/[0.03] hover:bg-ink/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
